import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { isNil } from 'lodash';
import { useTranslation } from '@lib/useTypedTranslation';
import { ITableColumn } from '../../../../components/data-table/types';
import { formatDateLastUpdated, formatValue, getFormatNumberToLocale } from '../../../../components/data-table/dataFormatters';
import { useWorldSettingsContext } from '../../../../context/worldSettings';
import { useWorldRoutes } from '../../../../routes/parts/allWorldRoutes';
import { rearrangeList } from '../../../../lib/rearrangeList';
import { encode } from '../../../../lib/base64';
import { useLanguageAndLocaleContext } from '../../../../context/languageAndLocale';
import { IPerformanceEventDeviceRow } from '../../../../services/core/performanceEventDevices';
import { GroupListFilter, HomeLocationsListFilter, ManufacturerListFilter, ModelListFilter } from './filters/corePerformanceDevicesFilters';

const getDeviceIdLink = (value: any, rowData: IPerformanceEventDeviceRow, path: string) => {
  if (isNil(rowData)) {
    return formatValue(value);
  }
  return <Link to={`${path}?id=${encodeURIComponent(encode(rowData.id))}`} className='core-performance-devices-list_link'>{formatValue(value)}</Link>;
};

interface UseCorePerformanceDevicesColumnsOptions {
  countColumnTitle: string | JSX.Element,
  columnId?: string,
  columnDataPath?: string,
  valueProcessor?: (value: any, rowData: IPerformanceEventDeviceRow) => any
}

export const useCorePerformanceDevicesColumns = ({ countColumnTitle, columnId = 'count', columnDataPath = columnId, valueProcessor }: UseCorePerformanceDevicesColumnsOptions) => {
  const { t } = useTranslation(['translation']);
  const { worldSettings } = useWorldSettingsContext();
  const routes = useWorldRoutes();
  const { locale } = useLanguageAndLocaleContext();

  const columns = useMemo(() => {
    function getDeviceIdLinkProcessor(value: any, rowData: IPerformanceEventDeviceRow) {
      return getDeviceIdLink(value, rowData, routes.core.deviceOverview);
    }
    function getHomeLocationName(value: string, rowData: IPerformanceEventDeviceRow) {
      return rowData.homeLocation ? formatValue(rowData.homeLocation.name) : formatValue(null);
    }

    const cols: ITableColumn[] = [
      { id: 'serialNumber', title: t('SERIAL_NUMBER'), dataPath: 'serialNumber', sortable: true, processor: formatValue },
      { id: 'assetTag', title: t('ASSET_TAG'), dataPath: 'assetTag', sortable: true, processor: formatValue, minWidth: '10rem' },
      { id: 'imei', title: t('IMEI'), dataPath: 'imei', sortable: true, processor: formatValue, minWidth: '10rem' },
      { id: 'deviceName', title: t('DEVICE_NAME'), dataPath: 'deviceName', sortable: true, processor: formatValue },
      { id: columnId, title: countColumnTitle, dataPath: columnDataPath, sortable: true, sortDirection: 'desc', processor: valueProcessor || getFormatNumberToLocale(locale), textAlign: 'right' },
      { id: 'manufacturer', title: t('MANUFACTURER'), dataPath: 'manufacturer', sortable: true, processor: formatValue, FilterForm: ManufacturerListFilter },
      { id: 'model', title: t('MODEL'), dataPath: 'model', sortable: true, processor: formatValue, FilterForm: ModelListFilter, minWidth: '8rem' },
      { id: 'group', title: t('GROUPS'), dataPath: 'group', processor: formatValue, FilterForm: GroupListFilter, minWidth: '10rem' },
      { id: 'homeLocation', title: t('HOME_LOCATION'), dataPath: 'homeLocation.name', sortable: true, processor: getHomeLocationName, FilterForm: HomeLocationsListFilter },
      { id: 'firmware', title: t('FIRMWARE_VERSION'), dataPath: 'firmware.version', sortable: true, processor: formatValue, minWidth: '10rem' },
      { id: 'operatingSystem', title: t('OS_VERSION'), dataPath: 'operatingSystem.buildVersion', sortable: true, processor: formatValue },
      { id: 'biosVersion', title: t('BIOS_VERSION'), dataPath: 'bios.version', sortable: true, processor: formatValue },
      { id: 'updated', title: t('LAST_SEEN'), dataPath: 'updated', sortable: true, processor: formatDateLastUpdated, minWidth: '8rem' }
    ];

    const deviceIdentifier = worldSettings.deviceIdentifier;
    const updatedColumns = rearrangeList(cols, deviceIdentifier);
    updatedColumns[0] = { ...updatedColumns[0], processor: getDeviceIdLinkProcessor };

    return updatedColumns;
  }, [countColumnTitle, columnId, columnDataPath, valueProcessor, worldSettings, routes.core.deviceOverview, t, locale]);

  return columns;
};
